<template>
  <SoWidget class="w-100" :loading="isSearching" data-testid="tq-search-widget">
    <SoForm
      ref="form"
      v-model="isValid"
      data-testid="tq-search-form"
      @submit="search"
    >
      <label for="tariff-quoter-search-type">Search by Postcode or GSP</label>
      <div class="d-flex flex-wrap gap-1">
        <SoSelect
          id="tariff-quoter-search-type"
          v-model="tariffQuoter.searchTerm"
          :items="['Postcode', 'GSP']"
          type="simple"
        />
        <SoTextField
          v-if="!tariffQuoter.isGSPSearch"
          v-model="postcode"
          placeholder="e.g. W4 5PY"
          type="text"
          :rules="[rules.postcode, rules.required]"
          style="flex-grow: 1"
          autofocus
          data-testid="tq-postcode-select"
        />
        <SoSelect
          v-else
          v-model="tariffQuoter.gsp"
          placeholder="e.g. _C (London)"
          type="simple"
          :items="gspOptions"
          :rules="[rules.required]"
          style="flex-grow: 1"
          autofocus
          data-testid="tq-gsp-select"
        />
        <SoButton
          :loading="isSearching"
          :disabled="isValid === false"
          icon="search"
          type="primary"
          submit
          large
          data-testid="tq-search-btn"
        />
        <SoButton icon="close" type="danger" large @click="reset()" />
      </div>
      <template v-if="!tariffQuoter.isGSPSearch">
        <template v-if="meterpointsForPostcode.length && !isSearching">
          <div class="mt-4">
            <SoSelect
              v-if="!tariffQuoter.isManuallySelected"
              v-model="address"
              label="Property Address"
              :items="allAddressOptions"
              type="simple"
              class="w-100"
              data-testid="tq-address-options"
            />
            <template v-else>
              <SoSelect
                v-model="tariffQuoter.gsp"
                label="GSP"
                :items="gspOptions"
                type="simple"
                class="w-100 mt-4"
                data-testid="tq-manual-gsp-select"
              />
              <SoSelect
                v-model="manuallySelectedElec"
                label="Electricity"
                :items="elecAddressOptions"
                clearable
                type="multiple"
                class="w-100"
                data-testid="tq-mpan-address-select"
              />
              <SoSelect
                v-model="manuallySelectedGas"
                label="Gas"
                :items="gasAddressOptions"
                clearable
                type="multiple"
                class="w-100"
                data-testid="tq-mprn-address-select"
              />
            </template>
          </div>
          <div class="d-flex justify-space-between align-center">
            <SoCheckbox
              v-model="tariffQuoter.isManuallySelected"
              label="Manually Select"
            />
            <div
              v-if="!!selected?.gsp && !tariffQuoter.isManuallySelected"
              class="text-disabled"
              data-testid="tq-gsp-label"
            >
              <span class="font-weight-medium">GSP</span>:
              {{ tariffQuoter.gsp }} ({{ tariffQuoter.gspName }})
            </div>
          </div>
        </template>
        <div
          v-else-if="
            tariffQuoter.meterpointsForPostcode.isFetched &&
            !tariffQuoter.meterpointsForPostcode.isError &&
            !meterpointsForPostcode.length
          "
          class="mt-4 pa-4 rounded bg-surface-dim"
          data-testid="tq-no-addresses"
        >
          No addresses found, please try searching by GSP
        </div>
        <SoAlert
          v-if="tariffQuoter.meterpointsForPostcode.isError && !isSearching"
          type="error"
          class="mt-4"
          data-testid="tq-search-error"
          >The address search failed. Please try again, or search by
          GSP.</SoAlert
        >
        <SoAlert
          v-if="
            selected &&
            !selected?.gsp &&
            !tariffQuoter.isManuallySelected &&
            tariffQuoter.meterpointsForPostcode.isFetched
          "
          type="error"
          class="mt-4"
          >No GSP found for this postcode. Please manually select a GSP to
          proceed.
        </SoAlert>
        <SoAlert
          v-if="selected?.mpans.length && selected?.mprns.length"
          type="success"
          class="mt-4"
          >Electricity & gas meterpoints have been located at the
          property.</SoAlert
        >
        <SoAlert v-else-if="selected?.mpans.length" type="info" class="mt-4"
          >Only electricity meterpoint has been found at the property.</SoAlert
        >
        <SoAlert v-else-if="selected?.mprns.length" type="info" class="mt-4"
          >Only gas meterpoint has been found at the property.</SoAlert
        >
      </template>
    </SoForm>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { FuelType, useTariffQuoter } from "@/stores/tariffQuoter"
  import {
    IMeterpointAddress,
    IMPANAddress,
    IMPRNAddress,
  } from "@/models/meterpointAddress/meterpointAddress.types"
  import { GSP_MAP } from "@/constants/meterpoints"
  import { SoSelectObjectItem } from "@/types/components/SoSelect"
  import SoWidget from "@/components/SoWidget.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import rules from "@/validators/rules"
  import { postcodeFormatter } from "@/helpers/postcodeFormatterHelper"

  export default defineComponent({
    components: {
      SoWidget,
      SoForm,
      SoSelect,
      SoCheckbox,
      SoTextField,
      SoButton,
      SoAlert,
    },
    data() {
      return {
        tariffQuoter: useTariffQuoter(),
        postcode: null,
        address: null,
        manuallySelectedElec: [],
        manuallySelectedGas: [],
        isValid: false,
        rules: rules,
      }
    },
    computed: {
      isSearching() {
        return this.tariffQuoter.meterpointsForPostcode.isFetching
      },
      isGSPSearch() {
        return this.tariffQuoter.isGSPSearch
      },
      meterpointsForPostcode() {
        return this.tariffQuoter.meterpointsForPostcode.data
      },
      allAddressOptions() {
        return this.meterpointsForPostcode.map((m) => m.address.formatted)
      },
      elecAddressOptions() {
        return this.meterpointsForPostcode
          .filter((m) => m.mpans.length)
          .map((m) => m.address.formatted)
      },
      gasAddressOptions() {
        return this.meterpointsForPostcode
          .filter((m) => m.mprns.length)
          .map((m) => m.address.formatted)
      },
      gspOptions() {
        const options = [] as SoSelectObjectItem[]
        GSP_MAP.forEach((value, key) => {
          options.push({
            title: `${key} (${value})`,
            value: key,
          })
        })
        return options
      },
      selected(): IMeterpointAddress | null {
        if (this.tariffQuoter.isManuallySelected) {
          const elec = this.findAddress(this.manuallySelectedElec[0])
          const gas = this.findAddress(this.manuallySelectedGas[0])

          const mpans = this.manuallySelectedElec.map(
            (m) => this.findAddress(m)?.mpans[0] ?? ({} as IMPANAddress),
          )
          const mprns = this.manuallySelectedGas.map(
            (m) => this.findAddress(m)?.mprns[0] ?? ({} as IMPRNAddress),
          )

          if (elec) {
            return {
              ...elec,
              mpans: mpans,
              mprns: mprns,
              gsp: this.tariffQuoter.gsp ?? "",
            }
          } else if (gas) {
            return {
              ...gas,
              mpans: mpans,
              mprns: mprns,
              gsp: this.tariffQuoter.gsp ?? "",
            }
          } else {
            return null
          }
        } else {
          return this.findAddress(this.address)
        }
      },
    },
    watch: {
      isGSPSearch(val) {
        if (!val) {
          this.tariffQuoter.gsp = this.meterpointsForPostcode?.[0]?.gsp
          this.tariffQuoter.selectedFuels = [
            this.selected?.mpans.length ? "electricity" : null,
            this.selected?.mprns.length ? "gas" : null,
          ].filter((fuel) => fuel !== null) as FuelType[]
        } else {
          this.tariffQuoter.selectedFuels = ["electricity", "gas"]
        }

        this.tariffQuoter.quoteForMeterpoints = false
      },
      meterpointsForPostcode(val: IMeterpointAddress[]) {
        this.tariffQuoter.gsp = val?.[0]?.gsp
      },
      selected(val: IMeterpointAddress | null) {
        this.tariffQuoter.setSelectedMeterpoint(val)
        this.tariffQuoter.setMeterpointFuelUsage()
        this.tariffQuoter.setSelectedTariffs()
      },
    },
    mounted() {
      this.tariffQuoter.$reset()
    },
    methods: {
      search() {
        if (this.tariffQuoter.isGSPSearch) return
        const postcodeFormatted = postcodeFormatter(this.postcode ?? "")
        if (postcodeFormatted !== this.tariffQuoter.postcode) {
          this.clearInputs()
        } else if (this.tariffQuoter.meterpointsForPostcode.isError) {
          // The postcode is unchanged, so nothing would re-run the failed query.
          this.tariffQuoter.meterpointsForPostcode.refetch()
        }

        this.tariffQuoter.postcode = postcodeFormatted
      },
      reset() {
        this.tariffQuoter.$reset()
        this.postcode = null
        this.clearInputs()
      },
      clearInputs() {
        this.address = null
        this.manuallySelectedElec = []
        this.manuallySelectedGas = []
      },
      findAddress(address: string | null): IMeterpointAddress | null {
        return (
          this.meterpointsForPostcode.find(
            (mpxn) => mpxn.address.formatted === address,
          ) ?? null
        )
      },
    },
  })
</script>

<style lang="css" scoped>
  :deep(.v-card) {
    padding: 16px !important;
  }
</style>
